import {
  type CreatePackageDayRequest,
  type PackageDayOut,
  type PackageDetail,
} from '@utazzo/shared';
import { UzButton } from '@utazzo/ui';
import { CalendarDays, ChevronDown, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

import { useAddPackageDay, useDeletePackageDay, useUpdatePackageDay } from '../use-packages';

import { PackageDayFormDrawer } from './PackageDayFormDrawer';

interface DaysEditorProps {
  /** Live (edit) mode: the existing package whose days are edited via the API. */
  pkg?: PackageDetail | null;
  /** Controlled (create) mode: ordered draft days held in the parent. */
  draftValue?: CreatePackageDayRequest[];
  /** Controlled (create) mode: provide to switch to in-memory editing (no API). */
  onDraftChange?: (days: CreatePackageDayRequest[]) => void;
  /** Package's selected destination - the day's city list is filtered to it. */
  destinationId?: string | null;
}

/**
 * Re-stamp positional fields so the staged list stays consistently numbered:
 * `dayIndex` is 1-based by position, `sortOrder` is 0-based by position.
 */
const renumber = (days: CreatePackageDayRequest[]): CreatePackageDayRequest[] =>
  days.map((day, i) => ({ ...day, dayIndex: i + 1, sortOrder: i }));

/**
 * Build a {@link PackageDayOut}-shaped row from a draft day so the list view and
 * the edit drawer (which read `PackageDayOut`) work uniformly in create mode.
 * `id` is a synthetic positional key - draft days have no persisted id.
 */
const draftToRow = (day: CreatePackageDayRequest, i: number): PackageDayOut => ({
  id: `draft-${i}`,
  dayIndex: day.dayIndex,
  cityId: day.cityId ?? null,
  cityName: null,
  town: day.town ?? null,
  title: day.title ?? null,
  subtitle: day.subtitle ?? null,
  mealPlan: day.mealPlan ?? null,
  briefHtml: day.briefHtml ?? null,
  sortOrder: day.sortOrder ?? i,
});

export const DaysEditor = ({
  pkg,
  draftValue,
  onDraftChange,
  destinationId,
}: DaysEditorProps): React.JSX.Element => {
  const controlled = onDraftChange !== undefined;
  const draftDays = draftValue ?? [];

  // Hooks must run unconditionally; in controlled mode the mutations are never fired.
  const add = useAddPackageDay(pkg?.id ?? '');
  const update = useUpdatePackageDay(pkg?.id ?? '');
  const del = useDeletePackageDay(pkg?.id ?? '');

  const [drawer, setDrawer] = useState<{
    mode: 'create' | 'edit';
    item?: PackageDayOut;
    /** Position of the edited draft day (controlled mode only). */
    draftIndex?: number;
  } | null>(null);

  const rows: PackageDayOut[] = controlled
    ? draftDays.map((day, i) => draftToRow(day, i))
    : (pkg?.days ?? []);

  const pending = controlled ? false : add.isPending || update.isPending || del.isPending;
  const nextIndex = rows.length > 0 ? Math.max(...rows.map((r) => r.dayIndex)) + 1 : 1;

  const submitDraft = (payload: CreatePackageDayRequest): void => {
    if (!onDraftChange) return;
    const next =
      drawer?.mode === 'edit' && drawer.draftIndex !== undefined
        ? draftDays.map((d, i) => (i === drawer.draftIndex ? payload : d))
        : [...draftDays, payload];
    onDraftChange(renumber(next));
    setDrawer(null);
  };

  const submit = async (payload: CreatePackageDayRequest): Promise<void> => {
    if (drawer?.mode === 'edit' && drawer.item) {
      await update.mutateAsync({ childId: drawer.item.id, payload });
    } else {
      await add.mutateAsync(payload);
    }
    setDrawer(null);
  };

  const remove = (row: PackageDayOut, i: number): void => {
    if (!window.confirm('Remove this day?')) return;
    if (controlled) {
      onDraftChange?.(renumber(draftDays.filter((_, idx) => idx !== i)));
      return;
    }
    del.mutate(row.id);
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-900">Days</h3>
        <UzButton size="sm" onClick={() => setDrawer({ mode: 'create' })} disabled={pending}>
          <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          Add day
        </UzButton>
      </div>

      {rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-6 text-center text-xs text-zinc-500">
          No days yet.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((row, i) => {
            const isOpen = drawer?.mode === 'edit' && drawer.draftIndex === i;
            const toggle = (): void =>
              setDrawer(isOpen ? null : { mode: 'edit', item: row, draftIndex: i });
            return (
              <React.Fragment key={row.id}>
                <li
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onClick={toggle}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle();
                    }
                  }}
                  className="flex cursor-pointer items-center gap-2.5 rounded-md border border-zinc-200 px-3 py-2 hover:bg-zinc-50"
                >
                  <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-md bg-zinc-900 px-1.5 text-xs font-semibold text-white tabular-nums">
                    {row.dayIndex}
                  </span>
                  <CalendarDays className="h-3.5 w-3.5 text-zinc-400" aria-hidden />
                  <span className="min-w-0 flex-1 truncate text-sm text-zinc-800">
                    {row.title ?? `Day ${row.dayIndex}`}
                  </span>
                  {row.mealPlan && (
                    <span className="text-xs text-zinc-500 tabular-nums">{row.mealPlan}</span>
                  )}
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(row, i);
                    }}
                    disabled={pending}
                    className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 hover:bg-red-50 hover:text-red-600"
                    aria-label="Remove day"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
                {isOpen && (
                  <li className="list-none">
                    <PackageDayFormDrawer
                      mode="edit"
                      item={row}
                      nextIndex={nextIndex}
                      pending={pending}
                      destinationId={destinationId}
                      onClose={() => setDrawer(null)}
                      onSubmit={controlled ? undefined : submit}
                      onSubmitDraft={controlled ? submitDraft : undefined}
                    />
                  </li>
                )}
              </React.Fragment>
            );
          })}
        </ul>
      )}

      {drawer?.mode === 'create' && (
        <PackageDayFormDrawer
          mode="create"
          nextIndex={nextIndex}
          pending={pending}
          destinationId={destinationId}
          onClose={() => setDrawer(null)}
          onSubmit={controlled ? undefined : submit}
          onSubmitDraft={controlled ? submitDraft : undefined}
        />
      )}
    </section>
  );
};
